import { Alert, Button, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { Fragment, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ConfirmDeletionDialog } from '../../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { AppName } from '../../../data-models/integrations-app.data-model';
import { QuickBooksFundConnection } from '../../../services/queries/MaggieIntegrationsQueries';
import { IntegrationDetailHeader } from '../components/IntegrationDetailHeader';
import { SettingsCard } from '../components/SettingsCard';
import { useQuickBooksConfigActions } from './useQuickBooksConfigActions';

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  realm_conflict: 'This QuickBooks company is already connected to a different fund.',
  connect_failed: 'Failed to connect to QuickBooks. Please try again.',
};

export function QuickBooksConfiguration() {
  const { funds, isLoading, isConfigError, connectFund, disconnectFund, isDisconnecting } =
    useQuickBooksConfigActions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [fundToDisconnect, setFundToDisconnect] = useState<QuickBooksFundConnection | null>(null);
  const [connectingFundId, setConnectingFundId] = useState<number | null>(null);
  const [connectError, setConnectError] = useState('');
  const [disconnectError, setDisconnectError] = useState('');

  const oauthError = searchParams.get('quickbooksError');
  const oauthErrorMessage = oauthError
    ? (OAUTH_ERROR_MESSAGES[oauthError] ?? OAUTH_ERROR_MESSAGES.connect_failed)
    : '';

  const description =
    'Connect each fund to its own QuickBooks company. Funds are connected one at a time; the connection applies to your entire organization.';

  const dismissOauthError = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('quickbooksError');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConnect = useCallback(
    async (fund: QuickBooksFundConnection) => {
      setConnectError('');
      setConnectingFundId(fund.fundId);
      try {
        await connectFund(fund.fundId);
      } catch {
        setConnectError(`Failed to start the QuickBooks connection for ${fund.fundName}. Please try again.`);
        setConnectingFundId(null);
      }
    },
    [connectFund]
  );

  const handleDisconnect = useCallback(async () => {
    if (!fundToDisconnect) {
      return;
    }
    setDisconnectError('');
    try {
      await disconnectFund(fundToDisconnect.fundId);
    } catch {
      setDisconnectError(
        `Failed to disconnect ${fundToDisconnect.fundName} from QuickBooks. Please try again.`
      );
    } finally {
      setFundToDisconnect(null);
    }
  }, [disconnectFund, fundToDisconnect]);

  if (isLoading) {
    return (
      <Stack alignItems='center' justifyContent='center' minHeight='400px'>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack width='100%' height='100%' p='12px' gap='1rem' overflow='auto'>
      <IntegrationDetailHeader appName={AppName.QuickBooks} description={description} />

      {oauthErrorMessage && (
        <Alert severity='error' onClose={dismissOauthError}>
          {oauthErrorMessage}
        </Alert>
      )}
      {connectError && (
        <Alert severity='error' onClose={() => setConnectError('')}>
          {connectError}
        </Alert>
      )}
      {disconnectError && (
        <Alert severity='error' onClose={() => setDisconnectError('')}>
          {disconnectError}
        </Alert>
      )}

      <SettingsCard>
        <Typography variant='subtitle1' component='h6' sx={{ fontWeight: 500 }}>
          Fund Connections
        </Typography>
        {isConfigError ? (
          <Alert severity='error'>Failed to load QuickBooks fund connections. Please refresh the page.</Alert>
        ) : funds.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            No funds found. Create a fund before connecting to QuickBooks.
          </Typography>
        ) : (
          <Stack divider={<Divider />}>
            {funds.map((fund) => (
              <Fragment key={fund.fundId}>
                <Stack direction='row' alignItems='center' gap='0.75rem' py='0.5rem'>
                  <Typography variant='body2' sx={{ flexGrow: 1 }}>
                    {fund.fundName}
                  </Typography>
                  <Chip
                    label={fund.isConnected ? 'Connected' : 'Not Connected'}
                    color={fund.isConnected ? 'success' : 'default'}
                    size='small'
                    sx={{ borderRadius: '4px' }}
                  />
                  {fund.isConnected ? (
                    <Button
                      color='error'
                      variant='outlined'
                      size='small'
                      onClick={() => setFundToDisconnect(fund)}
                    >
                      Disconnect
                    </Button>
                  ) : (
                    <Button
                      variant='contained'
                      size='small'
                      disabled={connectingFundId !== null}
                      onClick={() => handleConnect(fund)}
                    >
                      Connect
                    </Button>
                  )}
                </Stack>
              </Fragment>
            ))}
          </Stack>
        )}
      </SettingsCard>

      {fundToDisconnect && (
        <ConfirmDeletionDialog
          open
          title={`Disconnect ${fundToDisconnect.fundName} from QuickBooks?`}
          onClose={() => setFundToDisconnect(null)}
          onConfirm={handleDisconnect}
          loading={isDisconnecting}
          confirmLabel='Yes, Disconnect'
        >
          <Typography variant='body2' color='text.secondary'>
            Data already synced from QuickBooks is retained, and the fund can be reconnected to the same
            QuickBooks company later.
          </Typography>
        </ConfirmDeletionDialog>
      )}
    </Stack>
  );
}
